<template>
    <listings-find
        :params="{ listing: $route.params.listing }"
        :include="['property,files(paginate:1,1|filters:with_erased=false)']"
        :preloaded="preloaded('listing')"
        :find-on-created="!preloaded('listing')"
        v-slot="{
            has_loaded,
            is_loading,
            data: listing,
            error,
            find
        }"
    >
        <users-current
            v-slot="{
                data: user
            }"
        >
            <div v-if="is_loading || !has_loaded">
                <base-loading
                    :retry="find"
                    :error="error"
                    class="py-6"
                />

                <error-modal
                    v-if="error && $error.is(error, { exclude: [400, 422] })"
                    :error="error"
                />
            </div>

            <template v-else>
                <div class="container max-w-xl mx-auto mb-12">
                    <h1 class="mb-6 text-2xl font-semibold leading-tight tracking-tight md:block xs:text-3xl font-noto-sans text-grey-darkest">
                        Message regarding

                        <base-address
                            :address="_.pick(listing.property.address, ['street', 'postcode'])"
                            :is-inline="true"
                            class="inline"
                        />
                    </h1>

                    <grid-layout class="flex-col-reverse mb-6 md:flex-row">
                        <grid-layout-item class="md:w-3/5">
                            <div class="mb-6 lg:pr-6">
                                <div class="mb-6">
                                    <p>
                                        Got a question? Send a message directly to the landlord/agent of
                                        <base-address
                                            :address="_.pick(listing.property.address, ['street', 'postcode'])"
                                            :is-inline="true"
                                            class="inline font-bold"
                                        />.
                                    </p>
                                </div>

                                <as-tenant
                                    v-if="tenants.length > 0"
                                    :listing="listing"
                                    :tenants="tenants"
                                    placeholder="I have a question about your property."
                                />

                                <as-user
                                    v-else-if="user"
                                    :listing="listing"
                                    :user="user"
                                    placeholder="I have a question about your property."
                                />

                                <as-guest
                                    v-else
                                    :listing="listing"
                                    placeholder="I have a question about your property."
                                />

                                <p class="mt-6 text-xs text-grey-darker">
                                    By sending this message, you agree to our
                                    <laroute-link
                                        :to="{ name: 'www.policies.terms.index' }"
                                        class="text-blue"
                                    >
                                        Terms,
                                    </laroute-link>

                                    <laroute-link
                                        :to="{ name: 'www.policies.privacy.index' }"
                                        class="text-blue"
                                    >
                                        Privacy
                                    </laroute-link>

                                    &amp;

                                    <laroute-link
                                        :to="{ name: 'www.policies.cookies.index' }"
                                        class="text-blue"
                                    >
                                        Cookie
                                    </laroute-link>

                                    policies.

                                    This site is protected by reCAPTCHA and the Google
                                    <a
                                        href="https://policies.google.com/privacy"
                                        class="text-blue"
                                    >
                                        Privacy Policy
                                    </a>
                                    and
                                    <a
                                        href="https://policies.google.com/terms"
                                        class="no-underline text-blue"
                                    >
                                        Terms of Service
                                    </a>
                                    apply.
                                </p>
                            </div>
                        </grid-layout-item>

                        <grid-layout-item class="w-2/5">
                            <div class="flex-wrap hidden mb-6 border rounded shadow md:flex border-grey-lightest">
                                <div class="block w-full h-48">
                                    <div
                                        :style="{
                                            'background-image': 'url(' + $laroute.route('storage.files.show', {
                                                file: listing.files.data[0].uuid
                                            }) + ')'
                                        }"
                                        class="h-full bg-center bg-cover rounded-t bg-grey-bg bg-grey-lightest"
                                    />
                                </div>

                                <div class="w-full">
                                    <div class="px-4 py-3 border-b-2 rounded rounded-l-none border-grey-lightest">
                                        <div class="flex items-center justify-between mb-1 font-bold text-label text-grey-dark">
                                            <div>
                                                {{ listing.number_of_bedrooms }} Bed {{ listing.property_type }}
                                            </div>
                                        </div>

                                        <div class="mb-0 text-2xl leading-tight text-grey-darkest">
                                            {{ listing.rent_amount | money(0) }}<!--
                                            --><span class="text-lg text-grey-dark"> / month</span>
                                        </div>

                                        <div class="mb-2 text-sm font-semibold text-grey-dark">
                                            + {{ listing.deposit_amount | money(0) }} deposit
                                        </div>

                                        <div class="text-base font-semibold">
                                            <base-address
                                                :address="listing.property.address"
                                                :is-inline="true"
                                                class="inline text-grey-darker"
                                            />
                                        </div>
                                    </div>

                                    <div class="flex justify-between px-4 py-4 text-xs md:px-8 text-grey-darker lg:text-sm">
                                        <div class="inline-flex items-center">
                                            <base-svg
                                                name="icon-sofa"
                                                class="w-5 mr-2 sm:w-6"
                                            />

                                            <span class="font-bold text-grey-darkest">{{ listing.number_of_receptions }}</span>
                                            &nbsp;Rcpt.
                                        </div>

                                        <div class="inline-flex items-center">
                                            <base-svg
                                                name="icon-bed"
                                                class="w-5 mr-2 sm:w-6"
                                            />

                                            <span class="font-bold text-grey-darkest">{{ listing.number_of_bedrooms }}</span>
                                            &nbsp;Bdrm.
                                        </div>

                                        <div class="inline-flex items-center">
                                            <base-svg
                                                name="icon-bath"
                                                class="w-5 mr-2 sm:w-6"
                                            />

                                            <span class="font-bold text-grey-darkest">{{ listing.number_of_bathrooms }}</span>
                                            &nbsp;Bthr.
                                        </div>
                                    </div>

                                    <div class="px-4 py-4 text-xs font-semibold leading-none md:px-6 bg-grey-lightest text-label xs:text-sm">
                                        <span class="xs:hidden">
                                            Avl.
                                        </span>
                                        <span class="hidden xs:inline">
                                            Available
                                        </span>
                                        <template v-if="listing.available_at && $moment(listing.available_at).diff($moment()) > 0">{{ listing.available_at | moment('D MMM YYYY') }}</template>
                                        <template v-else>Now</template>
                                    </div>
                                </div>
                            </div>
                        </grid-layout-item>
                    </grid-layout>
                </div>
            </template>
        </users-current>
    </listings-find>
</template>

<script>
    import _ from 'lodash'
    import HeadMixin from '../../../mixins/head'
    import PreloadedMixin from '../../../mixins/preloaded'
    import AsGuest from './create/as-guest'
    import AsTenant from './create/as-tenant'
    import AsUser from './create/as-user'
    import ListingsFind from '../../../components/listings-find'
    import UsersCurrent from '../../../components/users-current'

    export default {
        mixins: [
            HeadMixin,
            PreloadedMixin
        ],
        components: {
            AsGuest,
            AsTenant,
            AsUser,
            ListingsFind,
            UsersCurrent
        },
        computed: {
            tenants () {
                return _.get(_.preload('message'), 'tenants', [])
            }
        },
        methods: {
            head () {
                return {
                    title: [
                        'Message the landlord/agent'
                    ],
                    description: 'Pay your rent online, build your credit history, find your perfect home, view inventories and more.'
                }
            }
        }
    }
</script>
